import React, { useState, useEffect, useCallback, useContext, useRef} from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner
} from "react-bootstrap";

import useQuery from "src/Hooks/useQuery";
import AuthContext from "src/Context/authContext";
import { getBankList, getOtp, getOwnerSpots, getTransaction, getBankAccountName } from "src/Services/api";
import JamlyfTable from "src/Components/Table";
import Notification, { useNotification } from 'src/Components/Notification';
import OtpModal from "../Otp";

import "./styles.scss";

const TRANSACTIONS_TABLE_MAPPER = {
  description: "description",
  reference_number: "reference number",
  value: "amount",
  applicable_fee: "applicable fee",
  status: "status",
  reason_for_failure: "reason for failure"
};

const getTransactionQueryParams = (spotId, pageNumber) => {
  const queryParams = { spot: spotId };
  if (!!pageNumber) {
    queryParams["page"] = pageNumber;
  }
  return queryParams;
};

const WithdrawFunds = () => {
  const [withdrawValues, setWithdrawValues] = useState({});
  const [otpDetails, setOtpDetails] = useState({});
  const [balance, setBalance] = useState(0.0);
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [pageNum, setPageNum] = useState(1);
  const { user } = useContext(AuthContext);
  const { id: spotId } = useParams();
  const bankInputRef = useRef(null);
  const { notifyError, notifySuccess, onClose, notification } = useNotification();

  const { loading: bankLoading, response: bankListResponse = {} } = useQuery(getBankList);
  const { data: bankList = [] } = bankListResponse;

  const queryParams = getTransactionQueryParams(spotId, pageNum)
  const { response: transactionResponse = {} } = useQuery(getTransaction, queryParams);
  const { data = {} } = transactionResponse;
  const { objects: transactions = [], total_pages } = data;

  const getSpot = useCallback(async () => {
    setIsLoading(true);
    const resp = await getOwnerSpots(user.id, spotId);
    if (resp.status === 200) {
      setBalance(resp.data.wallet.balance ?? 0.0);
    } else {
      notifyError({ message: resp.response.data.detail });
    }
    setIsLoading(false);
  }, [user, spotId, notifyError]);

  const getBeneficiaryName = useCallback(async data => {
    setIsLoading(true);
    const res = await getBankAccountName(data);
    if (res.status === 200) {
      return res.data.account_name;
    } else {
      notifyError({ message: res.response.data.detail });
    }
  }, [notifyError]);

  const computeTableData = (transactions) => {
    return transactions.map((item) => {
      return Object.keys(TRANSACTIONS_TABLE_MAPPER).reduce((acc, key) => {
        acc[key] = item[key];
        return acc;
      }, {});
    });
  };

  const resetForm = () => {
    setWithdrawValues({})
    if (bankInputRef.current) {
      bankInputRef.current.value = "";
    }
  }

  useEffect(() => {
    if (user && Object.keys(user).length) {
      getSpot();
    };
  }, [user, getSpot]);

  useEffect(() => {
    if (transactions.length) {
      setTableData(computeTableData(transactions));
    }
  }, [transactions]);

  useEffect(() => {
    const { accountNumber: account_number, bank, beneficiaryName } = withdrawValues;
    if (!beneficiaryName && account_number && account_number.length === 10 && bank) {
      getBeneficiaryName({account_number, bank})
        .then(beneficiaryName => {
          setWithdrawValues({
            ...withdrawValues,
            beneficiaryName,
          });
        })
        .catch((error) => {
          notifyError({ message: error.response.data.detail });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [withdrawValues, getBeneficiaryName, notifyError]);

  const initiateWithdrawal = async () => {
    setIsLoading(true);
    const res = await getOtp(balance, spotId);
    if (res.status === 200) {
      const withdrawData = {
        applicable_fee: res.data.applicable_fee,
        reference_number: res.data.reference_number,
        spot: spotId,
        bank_name: withdrawValues.bank,
        bank_account_number: withdrawValues.accountNumber,
        narration: withdrawValues.narration,
        beneficiary_name: withdrawValues.beneficiaryName,
        amount_after_charges: res.data.amount_after_charges
      };
      setOtpDetails(withdrawData);
      setShowOtpModal(true);
    }
    setIsLoading(false);
  };

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (name === "bank") {
      const results = bankList.filter(obj => {
        return obj.name === value;
      });
      value = results.length > 0 ? results[0].code : ""
    }
    setWithdrawValues(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  return (
    <Container className="pt-4">
      <Notification id="withdraw-notif" onClose={onClose} {...notification} />
      <Row className="mb-4">
        <Col>
          <h5 className="pb-3">Withdraw funds</h5>
          <Card className="wallet-card shadow">
            <Card.Header className="d-inline-flex justify-content-between">
              <p className="balance card-text">
                Current Balance: &#8358;{balance.toFixed(2)}
              </p>
              <Button
                className="jamlyfSolidBtn"
                type="submit"
                onClick={initiateWithdrawal}
                disabled={
                  balance === 0 ||
                  isLoading ||
                  (
                    !withdrawValues.bank ||
                    !withdrawValues.accountNumber ||
                    !withdrawValues.beneficiaryName
                  )
                }
              >
                {isLoading ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : "Withdraw"}
              </Button>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group>
                  <Row className="mb-3">
                    <Col>
                      <Form.Label>Bank account number</Form.Label>
                      <Form.Control
                        type="number"
                        name="accountNumber"
                        value={withdrawValues.accountNumber || ""}
                        onChange={handleInputChange}
                        required
                      />
                    </Col>
                    <Col>
                      <Form.Label>Bank</Form.Label>
                      <Form.Control
                        onChange={handleInputChange}
                        name="bank"
                        list="banklistOptions"
                        placeholder={!bankLoading ? "Type to search" : "Loading..."}
                        ref={bankInputRef}
                        disabled={bankLoading}
                      />
                      <datalist id="banklistOptions">
                        {bankList.map(bank =>
                          <option key={bank.code}>{bank.name}</option>
                        )}
                      </datalist>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col xs={6}>
                      <Form.Label>Beneficiary name</Form.Label>
                      <Form.Control
                        type="text"
                        name="beneficiary_name"
                        value={withdrawValues.beneficiaryName || ""}
                        onChange={handleInputChange}
                        disabled
                      />
                    </Col>
                    <Col xs={6}>
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        type="text"
                        name="narration"
                        value={withdrawValues.narration || ""}
                        onChange={handleInputChange}
                      />
                    </Col>
                  </Row>
                </Form.Group>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <hr className="my-4"/>
      <Row>
        <Col>
          <div className="mb-3">
            <h5>Withdraw Transaction History</h5>
            <span className="text-muted fs-6">See history of your credit and debit payments</span>
          </div>
        </Col>
        {/* <Col>
          <Button className="jamlyfSolidBtn float-end">Download All</Button>
        </Col> */}
      </Row>
      <JamlyfTable
        data={tableData}
        headerMapper={TRANSACTIONS_TABLE_MAPPER}
        headers={Object.values(TRANSACTIONS_TABLE_MAPPER)}
        onPageChange={setPageNum}
        totalPages={total_pages}
      />
      <OtpModal
        details={otpDetails}
        setShowModal={setShowOtpModal}
        showModal={showOtpModal}
        notifySuccess={notifySuccess}
        clearForm={(e) => resetForm(e)}
      />
    </Container>
  );
};

export default WithdrawFunds;
